import React, { useState, useEffect, Suspense } from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { Toaster } from './ui/sonner';
// import { canAccessModule } from '../Services/AuthContext';

// Error Boundary Component
class ErrorBoundary extends React.Component{
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {

    console.error('Component Error:', error, errorInfo);
    // Log to help with debugging
    console.error('Error details:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="flex items-center justify-center min-h-[400px] p-4">
          <div className="glass-card p-8 text-center max-w-md mx-auto">
            <div className="text-worldsview-red mb-4 text-2xl">⚠️</div>
            <h3 className="font-medium mb-2">Something went wrong</h3>
            <p className="text-muted-foreground mb-4 text-sm">
              {this.state.error?.message || 'There was an error loading this component.'}
            </p>
            <div className="flex gap-2 justify-center">
              <button 
                onClick={() => this.setState({ hasError: false, error: undefined })} 
                className="px-4 py-2 bg-worldsview-green text-white rounded-md hover:bg-worldsview-green/90 transition-colors"
              >
                Try Again
              </button>
              <button 
                onClick={() => window.location.reload()} 
                className="px-4 py-2 bg-worldsview-blue text-white rounded-md hover:bg-worldsview-blue/90 transition-colors"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Simplified lazy loading with error handling
const lazyImport = (importFn, componentName ) => {
  return React.lazy(() => 
    importFn().catch(error => {
      console.error(`Failed to import ${componentName}:`, error);
      // Return a fallback component
      return {
        default: () => (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="glass-card p-8 text-center max-w-md mx-auto">
              <div className="text-worldsview-red mb-4 text-2xl">⚠️</div>
              <h3 className="font-medium mb-2">Failed to load {componentName}</h3>
              <p className="text-muted-foreground mb-4 text-sm">
                {error.message || 'An error occurred while loading this component.'}
              </p>
              <button 
                onClick={() => window.location.reload()} 
                className="px-4 py-2 bg-worldsview-blue text-white rounded-md hover:bg-worldsview-blue/90 transition-colors"
              >
                Reload Page
              </button>
            </div>
          </div>
        )
      };
    })
  );
};

// Import critical components directly (no lazy loading for better performance)
import { SalesRenewals } from './SalesRenewalsNew';
import { Dashboard } from './Dashboard';
import { Announcements } from './Announcements';
import { Profile } from './Profile';
import { FinancialTools } from './FinancialTools';
import { PriceLookup } from './PriceLookup';
import { UserManagement } from './UserManagement';
import { useAuth } from '../Services/AuthContext';

// Lazy load non-critical components with simplified error handling
const ContentLibrary = lazyImport(() => import('./ContentLibrary').then(module => ({ default: module.ContentLibrary })), 'ContentLibrary');
const ContentManagement = lazyImport(() => import('./ContentManagement').then(module => ({ default: module.ContentManagement })), 'ContentManagement');
const PartnerAdministration = lazyImport(() => import('./PartnerAdministration').then(module => ({ default: module.PartnerAdministration })), 'PartnerAdministration');
const PartnerManagement = lazyImport(() => import('./PartnerManagement').then(module => ({ default: module.PartnerManagement })), 'PartnerManagement');
// const UserManagement = lazyImport(() => import('./UserManagement').then(module => ({ default: module.UserManagement })), 'UserManagement');
const RoleManagement = lazyImport(() => import('./RoleManagement').then(module => ({ default: module.RoleManagement })), 'RoleManagement');
const IntegrationManagement = lazyImport(() => import('./IntegrationManagement').then(module => ({ default: module.IntegrationManagement })), 'IntegrationManagement');

// Preload non-critical components after initial render
if (typeof window !== 'undefined') {
  // Preload commonly used components
  setTimeout(() => {
    import('./ContentLibrary').catch(() => {});
    import('./ContentManagement').catch(() => {});
  }, 2000);
}

// Loading component for better UX
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="glass-card p-8 text-center max-w-md mx-auto">
      <div className="relative w-12 h-12 mx-auto mb-4">
        <div className="absolute inset-0 rounded-full border-4 border-worldsview-purple/20"></div>
        <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-worldsview-purple animate-spin"></div>
      </div>
      <p className="text-muted-foreground">Loading content...</p>
      <p className="text-xs text-muted-foreground/70 mt-2">Please wait</p>
    </div>
  </div>
);

function AppContent() {

  let {canAccessModule} = useAuth();

  const [activeSection, setActiveSection] = useState('Dashboard');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [openCreateOpportunity, setOpenCreateOpportunity] = useState(false);
  const [initialFilterEvent, setInitialFilterEvent] = useState(null);
  const [selectedAnnouncementId, setSelectedAnnouncementId] = useState(null);
  const [componentKey, setComponentKey] = useState(0);

  // Force component remount on section change
  useEffect(() => {
    setComponentKey(prev => prev + 1);
  }, [activeSection]);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Apply saved text size preference on app load
  useEffect(() => {
    const savedTextSize = localStorage.getItem('worldsview-text-size');
    if (savedTextSize) {
      const sizeMap = {
        'small': '12px',
        'medium': '14px',
        'large': '16px',
        'extra-large': '18px'
      };
      document.documentElement.style.setProperty('--font-size', sizeMap[savedTextSize] || '14px');
    } else {
      // Ensure default font-size is set if no preference is saved
      document.documentElement.style.setProperty('--font-size', '14px');
    }
  }, []);

  // Reset text size when navigating to ensure consistency
  useEffect(() => {
    const savedTextSize = localStorage.getItem('worldsview-text-size');
    const sizeMap = {
      'small': '12px',
      'medium': '14px',
      'large': '16px',
      'extra-large': '18px'
    };
    document.documentElement.style.setProperty('--font-size', sizeMap[savedTextSize] || '14px');
  }, [activeSection]);

  // Reset create opportunity flag when changing sections
  useEffect(() => {
    if (activeSection !== 'Sales & Renewals') {
      setOpenCreateOpportunity(false);
      setInitialFilterEvent(null);
    }
    if (activeSection !== 'Announcements') {
      setSelectedAnnouncementId(null);
    }
  }, [activeSection]);

  // Function to navigate to sales renewals with create dialog open
  const navigateToCreateOpportunity = () => {
    setActiveSection('Sales & Renewals');
    setOpenCreateOpportunity(true);
  };

  // Function to navigate to sales renewals with event filter
  const navigateToRenewals = () => {
    setActiveSection('Sales & Renewals');
    setInitialFilterEvent('Renewal');
  };

  // Function to navigate to announcements with a specific announcement selected
  const navigateToAnnouncement = (announcementId) => {
    setActiveSection('Announcements');
    setSelectedAnnouncementId(announcementId);
  };

  const renderActiveSection = () => {
    // Check if user has access to the current section
    console.log(`Checking access for ${activeSection} : ${canAccessModule(activeSection)}`)
    if (!canAccessModule(activeSection)) {
      // If user doesn't have access, redirect to dashboard
      setActiveSection('My Workspace');
      return <Dashboard setActiveSection={setActiveSection} navigateToCreateOpportunity={navigateToCreateOpportunity} navigateToRenewals={navigateToRenewals} navigateToAnnouncement={navigateToAnnouncement} />;
    }

    switch (activeSection) {
      case 'My Workspace':
        return <Dashboard setActiveSection={setActiveSection} navigateToCreateOpportunity={navigateToCreateOpportunity} navigateToRenewals={navigateToRenewals} navigateToAnnouncement={navigateToAnnouncement} />;
      case 'Announcements':
        return <Announcements initialAnnouncementId={selectedAnnouncementId} />;
      case 'Information Hub':
        return <ContentLibrary />;
      case 'Finance':
        return <FinancialTools setActiveSection={setActiveSection} />;
      case 'Price Lookup':
        return <PriceLookup setActiveSection={setActiveSection} navigateToCreateOpportunity={navigateToCreateOpportunity}/>;
      case 'Sales & Renewals':
        return <SalesRenewals setActiveSection={setActiveSection} openCreateDialog={openCreateOpportunity} initialFilterEvent={initialFilterEvent} />;
      case 'Content Management':
        return canAccessModule('Content Management') ? <ContentManagement /> : <Dashboard setActiveSection={setActiveSection} navigateToCreateOpportunity={navigateToCreateOpportunity} navigateToRenewals={navigateToRenewals} navigateToAnnouncement={navigateToAnnouncement} />;
      case 'Partner Administration':
        return <PartnerAdministration />;
      case 'Partner Management':
        return canAccessModule('Partner Management') ? <PartnerManagement /> : <Dashboard setActiveSection={setActiveSection} navigateToCreateOpportunity={navigateToCreateOpportunity} navigateToRenewals={navigateToRenewals} navigateToAnnouncement={navigateToAnnouncement} />;
      case 'User Management':
        return canAccessModule('User Management') ? <UserManagement /> : <Dashboard setActiveSection={setActiveSection} navigateToCreateOpportunity={navigateToCreateOpportunity} navigateToRenewals={navigateToRenewals} navigateToAnnouncement={navigateToAnnouncement} />;
      case 'Role Management':
        return canAccessModule('Role Management') ? <RoleManagement /> : <Dashboard setActiveSection={setActiveSection} navigateToCreateOpportunity={navigateToCreateOpportunity} navigateToRenewals={navigateToRenewals} navigateToAnnouncement={navigateToAnnouncement} />;
      case 'profile':
        return <Profile />;
      case 'integration-management':
        return canAccessModule('integration-management') ? <IntegrationManagement /> : <Dashboard setActiveSection={setActiveSection} navigateToCreateOpportunity={navigateToCreateOpportunity} navigateToRenewals={navigateToRenewals} navigateToAnnouncement={navigateToAnnouncement} />;
      default:
        return <Dashboard setActiveSection={setActiveSection} navigateToCreateOpportunity={navigateToCreateOpportunity} navigateToRenewals={navigateToRenewals} navigateToAnnouncement={navigateToAnnouncement} />;
    }
  };

  return (
    <div className="min-h-screen glass-background-optimized">
      <div className="flex flex-col relative z-10">
        <Header 
          isDarkMode={isDarkMode} 
          setIsDarkMode={setIsDarkMode}
          setActiveSection={setActiveSection}
          navigateToAnnouncement={navigateToAnnouncement}
        />
        <div className="flex flex-1">
          <Sidebar 
            activeSection={activeSection} 
            setActiveSection={setActiveSection}
          />
          <main className="flex-1 p-6 ml-64">
            <ErrorBoundary key={componentKey}>
              <Suspense fallback={<LoadingSpinner />}>
                <div className="animate-fade-in-fast" key={activeSection}>
                  {renderActiveSection()}
                </div>
              </Suspense>
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </div>
  );
}

export function WV() {
  return (
    <>
      <AppContent />
      <Toaster position="top-right" />
    </>
  );
}